import { useCallback, useMemo, useState } from 'react';
import { EMenuItemTypes, EMenuSubItemTypes, TMenuItem, TMenuSubItem, TSubMenuSpacer } from './menu-items';
import * as Icons from 'react-icons/bs';
import { Target } from '../shared/Panels';
import '../../scss/menu.scss';

import { useOutsideClick } from '../../hooks/useOutsideClick';

const SubMenuItem = (props: TMenuSubItem | TSubMenuSpacer) => {
  const shorcutText = useMemo(() => {
    if (props.type === EMenuSubItemTypes.SPACER) return null;
    let text = "";
    if (props.shortcut) {
      if (props.shortcut.isCTRL) text = `Ctrl + `;
      if (props.shortcut.isALT) text = `${text}Alt + `;
      if (props.shortcut.isShift) text = `${text}Shift + `;
      text = `${text}${props.shortcut.key}`
    }
    return text;
  }, [props]);

  if (props.type === EMenuSubItemTypes.SPACER)
    return <div className={`menubar-item spacer ${props.orientation}`}></div >


  return (
    <div className={`menubar-subitem ${props.disabled ? 'disabled' : ''}`} onClick={(e) => { e.stopPropagation(); e.preventDefault(); props.onClick(e); }} onMouseDown={(e) => e.preventDefault()}>
      <div className='menubar-subitem-icon'>
        {props.ticked ? <Icons.BsCheck /> : null}
      </div>
      <div className={`menubar-subitem-text ${props.truncate ? 'truncate' : ''}`}>
        {props.label}
      </div>
      <div className='menubar-subitem-shortcut'>
        {shorcutText}
      </div>
    </div>
  )
}

const MenuItem = (props: TMenuItem) => {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClick(() => setOpen(false));

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    if (props.type === EMenuItemTypes.SPACER) return;
    if (props.contents.length) {
      setOpen(true);
    }
    props.onClick?.()
  }, [props])

  if (props.type === EMenuItemTypes.SPACER)
    return <div className={`menubar-item spacer ${props.orientation}`}></div >

  const relevantContents = props.contents.filter(si => si.visible);

  if (props.hideIfNoContents && relevantContents.length === 0) return null;

  return (
    <div className={`menubar-item ${props.type === EMenuItemTypes.ICON ? 'icon-only' : ''} ${props.disabled ? 'disabled' : ''}`} onClick={(e) => { if (!props.disabled) handleClick(e) }} onMouseDown={(e) => e.preventDefault()}>
      {
        props.type === EMenuItemTypes.ICON || props.type === EMenuItemTypes.TEXT_ICON
          ? props.icon
          : null
      }
      {
        props.type === EMenuItemTypes.TEXT || props.type === EMenuItemTypes.TEXT_ICON
          ? <span className='menubar-item-text'>{props.label}</span>
          : null
      }
      {
        open && relevantContents.length > 0
          ? <div className={`menubar-sub-item-wrapper`} ref={ref}>
            {
              relevantContents.map((si, idx) =>
                si.type === EMenuSubItemTypes.SUB_ITEM
                  ? <SubMenuItem key={idx} {...si} onClick={(e) => { e.preventDefault(); if (si.type === EMenuSubItemTypes.SUB_ITEM) { setOpen(false); si.onClick(e); } }} />
                  : <SubMenuItem key={idx} {...si} />
              )
            }
          </div>
          : null
      }

    </div>
  )
}

const Menu = ({
  pre,
  preText,
  data,
  post,
  showBottomSeparator = false,
  itemPadding = 'sm',
  target,
}: {
  pre?: TMenuItem[],
  preText?: any,
  data: TMenuItem[],
  post?: TMenuItem[],
  target?: Target,
  showBottomSeparator?: boolean
  itemPadding?: 'sm' | 'md' | 'lg'
}) => {
  return (
    <div className={`menubar ${target} padding-${itemPadding}`}>
      {
        pre || preText
          ? <div className='menubar-pre-wrapper'>
            {
              preText || null
            }
            {pre ? pre.map((entry, idx) => <MenuItem key={idx} {...entry} />) : null}
          </div>
          : null
      }
      {
        data.length > 0
          ? <div className='menubar-data-wrapper'>
            {
              data.map((entry, idx) => <MenuItem key={idx} {...entry} />)
            }
          </div>
          : null
      }
      {
        post
          ? <>
            <div className='menubar-post-wrapper'>
              {post.map((entry, idx) => <MenuItem key={idx} {...entry} />)}
            </div>
          </>
          : null
      }
      {
        showBottomSeparator
          ? <div className='vintage-separator bottom'></div>
          : null
      }
    </div>
  )
}

export default Menu;
